import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, CheckCircle, XCircle, Eye, DollarSign, ArrowUpRight, ArrowDownRight, Clock, Wallet, CreditCard, Building2 } from 'lucide-react';

const PAYMENT_LABELS = {
  crypto_usdt_bep20: 'Crypto — USDT BEP20', crypto_usdt_trc20: 'Crypto — USDT TRC20',
  upi: 'UPI Transfer', bank_transfer: 'Bank Transfer', cheque: 'Cheque', cash: 'Cash (Branch)',
  usdt_bep20: 'USDT BEP20', manual: 'Manual'
};

const emptyFund = { client_id: '', account_id: '', type: 'deposit', amount: '', currency: 'USDT', payment_method: 'crypto_usdt_bep20', transaction_hash: '', wallet_address: '', upi_ref: '', bank_ref: '', notes: '' };

export default function FundsPage() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailTxn, setDetailTxn] = useState(null);
  const [form, setForm] = useState(emptyFund);
  const [filter, setFilter] = useState('all');
  const [approvalNotes, setApprovalNotes] = useState('');

  const fetchData = async () => {
    try {
      const [tRes, cRes, aRes] = await Promise.all([api.get('/funds'), api.get('/clients'), api.get('/accounts')]);
      setTransactions(tRes.data); setClients(cRes.data); setAccounts(aRes.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    try {
      await api.post('/funds', { ...form, amount: parseFloat(form.amount) });
      toast.success('Transaction created'); setDialogOpen(false); setForm(emptyFund); fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleApproval = async (id, status) => {
    try {
      await api.put(`/funds/${id}/approve`, { status, notes: approvalNotes });
      toast.success(`Transaction ${status}`); setDetailTxn(null); setApprovalNotes(''); fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || '-';
  const filtered = filter === 'all' ? transactions : transactions.filter(t => t.status === filter);
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);

  const totalDeposits = transactions.filter(t => t.type === 'deposit' && t.status === 'approved').reduce((s, t) => s + (t.amount || 0), 0);
  const totalWithdrawals = transactions.filter(t => t.type === 'withdrawal' && t.status === 'approved').reduce((s, t) => s + (t.amount || 0), 0);
  const pendingCount = transactions.filter(t => t.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="funds-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-brand-text-primary tracking-tight">Fund Management</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Deposits, withdrawals, and approval workflows</p>
        </div>
        <Button onClick={() => { setForm(emptyFund); setDialogOpen(true); }} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg" data-testid="add-fund-btn">
          <Plus size={16} className="mr-2" /> New Transaction
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        {[
          { l: 'Total Deposits', v: `$${totalDeposits.toLocaleString()}`, icon: ArrowUpRight, c: 'text-brand-success' },
          { l: 'Total Withdrawals', v: `$${totalWithdrawals.toLocaleString()}`, icon: ArrowDownRight, c: 'text-brand-error' },
          { l: 'Net Flow', v: `$${(totalDeposits - totalWithdrawals).toLocaleString()}`, icon: DollarSign, c: 'text-brand-primary' },
          { l: 'Pending', v: pendingCount, icon: Clock, c: 'text-amber-600', prefix: '' },
        ].map(s => (
          <Card key={s.l} className="border border-brand-border bg-white metric-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-brand-bg flex items-center justify-center"><s.icon size={16} className={s.c} /></div>
              <div><p className="text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{s.l}</p><p className={`font-heading text-xl font-bold ${s.c}`}>{s.v}</p></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'approved', 'rejected'].map(f => (
          <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)}
            className={`capitalize rounded-lg text-xs ${filter === f ? 'bg-brand-primary text-white' : 'border-brand-border'}`} data-testid={`filter-${f}`}>{f} {f === 'pending' && pendingCount > 0 ? `(${pendingCount})` : ''}</Button>
        ))}
      </div>

      {/* Table */}
      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="funds-table">
              <thead><tr className="border-b border-brand-border bg-brand-bg/50">
                {['Client', 'Type', 'Method', 'Amount', 'Currency', 'Rate', 'Status', 'Date', 'Actions'].map(h => (
                  <th key={h} className="text-left py-3 px-3 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={9} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr> :
                 filtered.length === 0 ? <tr><td colSpan={9} className="py-12 text-center text-brand-text-secondary">No transactions found</td></tr> :
                 filtered.map(t => (
                  <tr key={t.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors cursor-pointer" onClick={() => { setDetailTxn(t); setApprovalNotes(''); }}>
                    <td className="py-3 px-3 text-brand-text-primary font-medium text-xs">{getClientName(t.client_id)}</td>
                    <td className="py-3 px-3"><Badge className={`text-[10px] border-0 ${t.type === 'deposit' ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'}`}>{t.type}</Badge></td>
                    <td className="py-3 px-3 text-xs text-brand-text-secondary">{PAYMENT_LABELS[t.payment_method] || t.payment_method || '-'}</td>
                    <td className="py-3 px-3 font-heading font-bold text-brand-text-primary">${Number(t.amount || 0).toLocaleString()}</td>
                    <td className="py-3 px-3 text-xs">{t.currency || 'USDT'}</td>
                    <td className="py-3 px-3 text-xs text-brand-text-secondary">{t.exchange_rate ? `₹${t.exchange_rate}` : '-'}</td>
                    <td className="py-3 px-3"><Badge className={`text-[10px] border-0 ${t.status === 'approved' ? 'bg-brand-success/15 text-brand-success' : t.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-brand-error/15 text-brand-error'}`}>{t.status}</Badge></td>
                    <td className="py-3 px-3 text-xs text-brand-text-secondary">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</td>
                    <td className="py-3 px-3">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-brand-primary" data-testid={`view-txn-${t.id}`}><Eye size={14} /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Detail Dialog */}
      <Dialog open={!!detailTxn} onOpenChange={() => setDetailTxn(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="txn-detail-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg flex items-center gap-2">
            {detailTxn?.type === 'deposit' ? <ArrowUpRight size={20} className="text-brand-success" /> : <ArrowDownRight size={20} className="text-brand-error" />}
            {detailTxn?.type === 'deposit' ? 'Deposit' : 'Withdrawal'} Details
          </DialogTitle></DialogHeader>
          {detailTxn && (
            <div className="space-y-4 py-2">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-3">
                <DetailRow label="Client" value={getClientName(detailTxn.client_id)} />
                <DetailRow label="TXN ID" value={detailTxn.transaction_id} mono />
                <DetailRow label="Amount" value={`$${Number(detailTxn.amount || 0).toLocaleString()} USDT`} bold />
                <DetailRow label="Status" value={detailTxn.status} badge />
                <DetailRow label="Currency" value={detailTxn.currency || 'USDT'} />
                <DetailRow label="Method" value={PAYMENT_LABELS[detailTxn.payment_method] || detailTxn.payment_method} />
              </div>

              {/* Conversion info */}
              {detailTxn.exchange_rate > 0 && (
                <div className="bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">Currency Conversion</p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div><p className="text-[10px] text-brand-text-secondary">Original</p><p className="font-medium">{detailTxn.original_amount} {detailTxn.currency}</p></div>
                    <div><p className="text-[10px] text-brand-text-secondary">Rate</p><p className="font-medium">₹{detailTxn.exchange_rate}</p></div>
                    <div><p className="text-[10px] text-brand-text-secondary">USDT</p><p className="font-medium">${detailTxn.usdt_amount}</p></div>
                  </div>
                </div>
              )}

              {/* Payment-specific details */}
              {(detailTxn.payment_method || '').startsWith('crypto') && (
                <div className="bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary mb-1 flex items-center gap-1"><Wallet size={12} /> Crypto Details</p>
                  {detailTxn.transaction_hash && <DetailRow label="TX Hash" value={detailTxn.transaction_hash} mono />}
                  {detailTxn.wallet_address && <DetailRow label="Wallet" value={detailTxn.wallet_address} mono />}
                </div>
              )}

              {detailTxn.payment_method === 'upi' && (
                <div className="bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary mb-1 flex items-center gap-1"><CreditCard size={12} /> UPI Details</p>
                  {detailTxn.upi_ref && <DetailRow label="UTR / Reference" value={detailTxn.upi_ref} mono />}
                  {detailTxn.upi_id && <DetailRow label="UPI ID" value={detailTxn.upi_id} />}
                </div>
              )}

              {detailTxn.payment_method === 'bank_transfer' && (
                <div className="bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary mb-1 flex items-center gap-1"><Building2 size={12} /> Bank Transfer Details</p>
                  {detailTxn.bank_ref && <DetailRow label="Bank Reference" value={detailTxn.bank_ref} mono />}
                  {detailTxn.bank_account_name && <DetailRow label="Account Holder" value={detailTxn.bank_account_name} />}
                  {detailTxn.bank_account_no && <DetailRow label="Account No" value={detailTxn.bank_account_no} mono />}
                  {detailTxn.bank_ifsc && <DetailRow label="IFSC Code" value={detailTxn.bank_ifsc} mono />}
                  {detailTxn.payment_destination && <DetailRow label="Payment To" value={detailTxn.payment_destination} />}
                </div>
              )}

              {(detailTxn.payment_method === 'cheque' || detailTxn.payment_method === 'cash') && (
                <div className="bg-brand-bg/60 rounded-lg p-3 border border-brand-border/50">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary mb-1">{detailTxn.payment_method === 'cheque' ? 'Cheque' : 'Cash'} Deposit</p>
                  {detailTxn.bank_ref && <DetailRow label="Reference" value={detailTxn.bank_ref} />}
                </div>
              )}

              {detailTxn.notes && <DetailRow label="Notes" value={detailTxn.notes} />}
              <DetailRow label="Created By" value={detailTxn.created_by_name || '-'} />
              <DetailRow label="Date" value={detailTxn.created_at ? new Date(detailTxn.created_at).toLocaleString() : '-'} />

              {/* Admin Approval */}
              {isAdmin && detailTxn.status === 'pending' && (
                <div className="border-t border-brand-border pt-4 space-y-3">
                  <Label className="text-xs">Approval Notes</Label>
                  <Textarea value={approvalNotes} onChange={e => setApprovalNotes(e.target.value)} placeholder="Optional notes..." rows={2} className="bg-white border-brand-border rounded-lg text-sm" />
                  <div className="flex gap-2">
                    <Button onClick={() => handleApproval(detailTxn.id, 'approved')} className="flex-1 bg-brand-success text-white rounded-lg" data-testid="approve-txn-btn">
                      <CheckCircle size={15} className="mr-1" /> Approve
                    </Button>
                    <Button onClick={() => handleApproval(detailTxn.id, 'rejected')} variant="outline" className="flex-1 rounded-lg text-brand-error border-brand-error/30" data-testid="reject-txn-btn">
                      <XCircle size={15} className="mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              )}

              {detailTxn.status === 'approved' && detailTxn.approved_by && (
                <div className="bg-brand-success/5 rounded-lg p-3 border border-brand-success/20">
                  <p className="text-xs text-brand-success font-semibold flex items-center gap-1"><CheckCircle size={12} /> Approved</p>
                  {detailTxn.approved_at && <p className="text-[10px] text-brand-text-secondary mt-0.5">On: {new Date(detailTxn.approved_at).toLocaleString()}</p>}
                  {detailTxn.approval_notes && <p className="text-xs text-brand-text-secondary mt-0.5">Notes: {detailTxn.approval_notes}</p>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Transaction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="fund-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg">New Transaction</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label className="text-xs">Client *</Label>
              <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-lg" data-testid="fund-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_id || ''} — {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Type *</Label>
                <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                  <SelectTrigger className="bg-white border-brand-border rounded-lg" data-testid="fund-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="deposit">Deposit</SelectItem><SelectItem value="withdrawal">Withdrawal</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label className="text-xs">Currency</Label>
                <Select value={form.currency} onValueChange={v => setForm({...form, currency: v})}>
                  <SelectTrigger className="bg-white border-brand-border rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="USDT">USDT</SelectItem><SelectItem value="INR">INR</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Payment Method</Label>
              <Select value={form.payment_method} onValueChange={v => setForm({...form, payment_method: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-lg" data-testid="fund-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="crypto_usdt_bep20">Crypto — USDT BEP20</SelectItem>
                  <SelectItem value="crypto_usdt_trc20">Crypto — USDT TRC20</SelectItem>
                  <SelectItem value="upi">UPI Transfer</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="cash">Cash (Branch Deposit)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Amount *</Label>
              <Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0.00" data-testid="fund-amount-input" className="bg-white border-brand-border rounded-lg" />
            </div>
            {form.payment_method?.startsWith('crypto') && (
              <div className="space-y-1.5"><Label className="text-xs">TX Hash</Label><Input value={form.transaction_hash} onChange={e => setForm({...form, transaction_hash: e.target.value})} placeholder="0x..." className="bg-white border-brand-border rounded-lg font-mono text-xs" /></div>
            )}
            {form.payment_method === 'upi' && (
              <div className="space-y-1.5"><Label className="text-xs">UPI Ref / UTR</Label><Input value={form.upi_ref} onChange={e => setForm({...form, upi_ref: e.target.value})} className="bg-white border-brand-border rounded-lg" /></div>
            )}
            {(form.payment_method === 'bank_transfer' || form.payment_method === 'cheque') && (
              <div className="space-y-1.5"><Label className="text-xs">Bank/Cheque Ref</Label><Input value={form.bank_ref} onChange={e => setForm({...form, bank_ref: e.target.value})} className="bg-white border-brand-border rounded-lg" /></div>
            )}
            <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="bg-white border-brand-border rounded-lg text-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={handleCreate} className="bg-brand-primary text-white rounded-lg" data-testid="fund-save-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const DetailRow = ({ label, value, mono, bold, badge }) => (
  <div className="py-1">
    <p className="text-[10px] uppercase tracking-wider text-brand-text-secondary font-semibold">{label}</p>
    {badge ? (
      <Badge className={`text-xs border-0 mt-0.5 ${value === 'approved' ? 'bg-brand-success/15 text-brand-success' : value === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-brand-error/15 text-brand-error'}`}>{value}</Badge>
    ) : (
      <p className={`text-sm ${mono ? 'font-mono text-xs break-all' : ''} ${bold ? 'font-heading font-bold text-brand-primary' : 'text-brand-text-primary'}`}>{value || '-'}</p>
    )}
  </div>
);
